import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import { APIList } from 'src/interfaces'
import { Entities } from 'src/enums'
import { ManufacturerAnnouncementCategory } from 'src/api'
import { useApiInstance } from './useApiInstance'
import {
  buildRequestParams,
  ObjectType,
  transformPage,
  transformPerPage,
} from './helpers'

interface RequestParams {
  params?: Partial<AdminManufacturerAnnouncementCategoriesGetParams>
  paramsTransform?: Array<(params: Record<string, any>) => Record<string, any>>
  options?: Omit<
    UseQueryOptions<APIList<ManufacturerAnnouncementCategory>, Error>,
    'queryKey' | 'queryFn'
  >;
}

interface AdminManufacturerAnnouncementCategoriesGetParams {
  limit: number
  offset: number
  sort: string
}

const schema: ObjectType['schema'] = {
  limit: 'number',
  offset: 'number',
  sort: 'string',
}

export const useManufacturerAnnouncementCategoryListRequest = ({
  params = {},
  paramsTransform = [],
  options,
}: RequestParams = {}) => {
  const api = useApiInstance()

  const transformedParams = [
    transformPerPage,
    transformPage,
    ...paramsTransform,
  ].reduce((param, transform) => transform(param), params)

  const queryParams = buildRequestParams<AdminManufacturerAnnouncementCategoriesGetParams>(
    transformedParams,
    schema,
  )

  return useQuery<APIList<ManufacturerAnnouncementCategory>, Error>(
    [Entities.ManufacturerAnnouncementCategory, JSON.stringify(transformedParams)],
    async () => {
      const {
        limit,
        offset,
        sort,
      } = queryParams
      const { data } = await api.v1AdminManufacturersAnnouncementsCategoriesGet(
        limit,
        offset,
        sort,
      )
      return data as APIList<ManufacturerAnnouncementCategory>
    },
    {
      enabled: Object.keys(transformedParams).length > 0,
      ...options,
    },
  )
}
